import React, { useEffect } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitleChildrenPath, TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import GetSpace from "../../../utils/space"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import {
  faAngleDown,
  faAngleUp,
  faCalendar,
  faClose,
  faDollarSign,
  faFile,
  faFileSignature,
  faMoneyBill,
  faPercent,
  faTags,
  faWarning,
} from "@fortawesome/free-solid-svg-icons"
import InputField from "../../../components/InputField/InputField"
import { useExpansive } from "../../../hooks/useExpansive"
import { MessageList } from "../../../components/MessageList/MessageList"
import { DateTimeSelector } from "../../../components/DateTimeSelector/DateTimeSelector"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { useRegister } from "../../../hooks/Register/useRegister"
import { Promotions } from "../../../Objects/Promotion/Promotions"
import { useCreatePromotions } from "../../../hooks/Promotions/useCreatePromotions"
import { useError } from "../../../context/useErrorContext"
import { useSavePromotions } from "../../../hooks/Promotions/useSavePromotions"
import { useGetPromotions } from "../../../hooks/Promotions/useGetPromotions"
import { useToggle } from "../../../hooks/useToggle"
import { ToggleComponet } from "../../../components/Toggle/ToggleComponet"
import { InputPhotoWithContainer } from "../../../components/InputPhoto/InputPhoto"
import { useFileImage } from "../../../hooks/useFileImage"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import THEME from "../../../configuration/Theme"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { EGenderType } from "../../../Enum/EGenderType"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { useRemovePromotions } from "../../../hooks/Promotions/useRemovePromotions"
import { SIZE } from "../../../configuration/Sizes"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { useTranslation } from "react-i18next"
import { EDayOfWeek } from "../../../Enum/EDayOfWeek"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { CanView } from "../../../components/CanView/CanView"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { AssociationsPopup } from "../../../components/Associations/AssociationsPopup"
import { comboMemory, catalogItemsMemory, productsMemory } from "../../../Objects/Memory/Memory"
import { resolvePromotionAssociations } from "../../../components/Associations/associationResolvers"
import { useApplyPromotions } from "../../../hooks/Promotions/useApplyPromotions"
import { PromotionApplyPopup } from "./PromotionApplyPopup"

interface PromotionsPupopProbs {
  title: string
  promotions?: Promotions
  onClonse: () => void
  onCopy?: () => void
}

export const PromotionsPupop: React.FC<PromotionsPupopProbs> = ({ promotions, title, onClonse, onCopy }) => {
  const { t } = useTranslation("promotion")

  const isCopiedDraft = promotions?.id === -1

  const [showAssociations, setShowAssociations] = React.useState(false)

  const canEdite = !usePermission(Permissions.CanEditePromotions)

  const { setNewConfirmation } = useConfirmation()

  const { handleRemove } = useRemovePromotions()

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const { handleGetPromotions } = useGetPromotions()

  const {
    name,
    description,
    percentagemDiscount,
    dayWeek,
    timeStart,
    timeFinish,
    GenderType: gender,
    coastFixed,
    status,
    handleNameChange,
    handleDescriptionChange,
    handlePercentagemDiscountChange,
    handleDayWeekChange,
    handleTimeFinishChange,
    handleTimeStartChange,
    handleStatusChange,
    handleGenderChange,
    handleCoastFixedChange,
    handleSubmitForm,
    handleInitialValue,
  } = useCreatePromotions(promotions)

  const { handleSavesPromotions } = useSavePromotions()

  const { setError } = useError()

  const { handleApplyPromotion, handleRemovePromotionApply } = useApplyPromotions()

  const [enableApplyPopup, setEnableApplyPopup] = React.useState(false)

  const [enableRemoveApplyPopup, setEnableRemoveApplyPopup] = React.useState(false)

  useEffect(() => {
    handleInitialValue(promotions)
  }, [promotions])

  const handleTrySubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const result = handleSubmitForm()

    if ("errors" in result) {
      const errorMessages = result.errors.map((e) => e.message).join("\n")
      setError(t("general:label_invalid"), errorMessages)

      return
    }

    handleSavesPromotions(result as Promotions, () => {
      onClonse()
      handleGetPromotions(() => {})
    })
  }

  const handleCopyClick = () => onCopy?.()

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              iconColor={THEME.colors.iconBGColor}
              icon={faTags}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <>
                    {promotions && promotions.id !== -1 && (
                      <div style={{ width: "100px", marginRight: "5px" }}>
                        <GenericDropDown
                          data={Object.mapEnumToStrings(EEnableStatusType, {
                            exclude: [EEnableStatusType.All],
                          })}
                          initialState={status}
                          disable={canEdite}
                          handlerFuncion={handleStatusChange}
                          descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.select.select"
                        />
                      </div>
                    )}

                    <PopupHelpButton
                      helpKey="pages.serivesdir.promotions.promotionspupop.1"
                      variant="global"
                      title={t("helpPopup:global.promotion.title")}
                      description={t("helpPopup:global.promotion.description")}
                      bullets={t("helpPopup:global.promotion.bullets", { returnObjects: true }) as string[]}
                    />

                    <GetSpace right={5} />

                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faClose}
                      onClick={onClonse}
                      size="small"
                      descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.button.onClonse"
                    />
                  </>
                </div>
              }
            />

            {isCopiedDraft && <CopyDraftAlert />}

            <form onSubmit={handleTrySubmit} className="p-1">
              <GetSpace top={5} />

              <BasicData
                name={name}
                disable={canEdite}
                description={description}
                gender={gender.toGenderTypeString()}
                handleDescriptionChange={handleDescriptionChange}
                handleGenderChange={handleGenderChange}
                handleNameChange={handleNameChange}
              />

              <div className={defaultStyle.divisionBar}></div>

              <CoastData
                disable={canEdite}
                coastFixed={coastFixed}
                percentagemDiscount={percentagemDiscount}
                handleCoastFixedChange={handleCoastFixedChange}
                handlePercentagemDiscountChange={handlePercentagemDiscountChange}
              />

              <div className={defaultStyle.divisionBar}></div>

              <DateData
                timeFinish={timeFinish}
                timeStart={timeStart}
                dayWeek={dayWeek}
                disable={canEdite}
                handleDayWeekChange={handleDayWeekChange}
                handleTimeFinishChange={handleTimeFinishChange}
                handleTimeStartChange={handleTimeStartChange}
              />

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditePromotions}>
                  <Button
                    label={t("buttons:button_save")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    iconColor={THEME.colors.buttonFGSaveColor}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.button.save"
                  />
                </CanView>

                {promotions && promotions.id !== -1 && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      label={t("buttons:button_register")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRegisterColor}
                      onClick={handleEnableRegister}
                      descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.button.register"
                    />

                    <GetSpace right={5} />

                    <Button
                      label={t("buttons:button_view_associations")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGPlusColor}
                      onClick={() => setShowAssociations(true)}
                      descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.button.viewAssociations"
                    />

                    <CanView permission={Permissions.CanEditePromotions}>
                      {onCopy && (
                        <>
                          <GetSpace right={5} />

                          <Button
                            label={t("buttons:button_copy")}
                            typeButton="button"
                            type="primary"
                            size="medium"
                            bgColor={THEME.colors.buttonBGPlusColor}
                            onClick={handleCopyClick}
                            descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.button.copy"
                          />
                        </>
                      )}
                    </CanView>
                  </>
                )}

                {promotions && promotions.id !== -1 && (
                  <CanView permission={Permissions.CanEditePromotions}>
                    <GetSpace right={5} />

                    <Button
                      label={t("buttons:button_remove")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      iconColor={THEME.colors.buttonFGRemoveColor}
                      bgColor={THEME.colors.buttonBGRemoveColor}
                      onClick={() =>
                        setNewConfirmation(
                          t("general:label_title_remove"),
                          t("general:label_description_remove"),
                          async () => {
                            await handleRemove(promotions!, () => handleGetPromotions(onClonse))
                          },
                        )
                      }
                      descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.button.remove"
                    />
                  </CanView>
                )}
              </div>

              <GetSpace top={5} />

              <div className={defaultStyle.buttonsController}>
                {promotions && promotions.id !== -1 && (
                  <CanView permission={Permissions.CanEditePromotions}>
                    <>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_apply")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        bgColor={THEME.colors.buttonBGRegisterColor}
                        onClick={() => setEnableApplyPopup(true)}
                        descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.button.apply"
                      />

                      <GetSpace right={5} />

                      <Button
                        label={t("button_remove_apply_automatic")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        bgColor={THEME.colors.buttonBGCloseColor}
                        onClick={() => setEnableRemoveApplyPopup(true)}
                        descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.button.removeApplyAutomatic"
                      />
                    </>
                  </CanView>
                )}
              </div>
            </form>

            {isRegisterEnable && promotions && (
              <ViewRegister id={promotions.id} typeEntity="PromotionEntity" onClose={handleDisableRegister} />
            )}

            {showAssociations && promotions && (
              <AssociationsPopup
                title={t("general:label_related_title", { name: promotions.name })}
                emptyMessage={t("general:label_related_empty")}
                onClose={() => setShowAssociations(false)}
                items={resolvePromotionAssociations(
                  promotions,
                  productsMemory.listData,
                  catalogItemsMemory.listData,
                  comboMemory.listData,
                )}
              />
            )}

            {enableApplyPopup && promotions && (
              <PromotionApplyPopup
                title={t("label_title_apply_automatic")}
                actionLabel={t("buttons:button_apply")}
                helpKey="pages.serivesdir.promotions.promotionspupop.2"
                helpTitle={t("helpPopup:sections.promotion.applyAutomatic.title")}
                helpDescription={t("helpPopup:sections.promotion.applyAutomatic.description")}
                helpBullets={
                  t("helpPopup:sections.promotion.applyAutomatic.bullets", { returnObjects: true }) as string[]
                }
                helpYoutubeLink={t("helpPopup:sections.promotion.applyAutomatic.video_link")}
                onClose={() => setEnableApplyPopup(false)}
                onConfirm={async (request) => {
                  await handleApplyPromotion(promotions.id, request, () => {
                    handleGetPromotions(() => {})
                    setEnableApplyPopup(false)
                  })
                }}
              />
            )}

            {enableRemoveApplyPopup && promotions && (
              <PromotionApplyPopup
                title={t("label_title_remove_apply_automatic")}
                actionLabel={t("button_remove_apply_automatic")}
                helpKey="pages.serivesdir.promotions.promotionspupop.3"
                helpTitle={t("helpPopup:sections.promotion.removeAutomatic.title")}
                helpDescription={t("helpPopup:sections.promotion.removeAutomatic.description")}
                helpBullets={
                  t("helpPopup:sections.promotion.removeAutomatic.bullets", { returnObjects: true }) as string[]
                }
                helpYoutubeLink={t("helpPopup:sections.promotion.removeAutomatic.video_link")}
                onClose={() => setEnableRemoveApplyPopup(false)}
                onConfirm={async (request) => {
                  await handleRemovePromotionApply(promotions.id, request, () => {
                    handleGetPromotions(() => {})
                    setEnableRemoveApplyPopup(false)
                  })
                }}
              />
            )}
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}

const BasicData: React.FC<{
  name: string
  description: string
  gender: string
  disable: boolean
  handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleGenderChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
}> = ({ name, description, gender, disable, handleNameChange, handleDescriptionChange, handleGenderChange }) => {
  const { t } = useTranslation("promotion")

  return (
    <>
      <GetSpace top={5} />

      <Animate type={AnimationTypes.fadeInTraslate}>
        <div>
          <GenericDropDown
            data={Object.mapEnumToStrings(EGenderType)}
            initialState={gender}
            handlerFuncion={handleGenderChange}
            disable={disable}
            descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.select.select2"
          />

          <GetSpace top={10} />

          <InputField
            type="text"
            value={name}
            icon={faFileSignature}
            maxLength={INPUT_SIZE.PROMOTIONS.name}
            placeholder={t("label_name_place_holder")}
            onInvalidMessage={t("label_name_onInvalidMessage")}
            onChange={handleNameChange}
            disable={disable}
            required
            descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.input.namePlaceHolder"
          />

          <GetSpace top={10} />

          <InputField
            type="text"
            icon={faFile}
            value={description}
            maxLength={INPUT_SIZE.PROMOTIONS.description}
            placeholder={t("label_description_place_holder")}
            onInvalidMessage={t("label_description_onInvalidMessage")}
            onChange={handleDescriptionChange}
            disable={disable}
            descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.input.descriptionPlaceHolder"
          />

          <GetSpace top={5} />
        </div>
      </Animate>
    </>
  )
}

const CoastData: React.FC<{
  percentagemDiscount: string
  coastFixed: string
  disable: boolean
  handlePercentagemDiscountChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleCoastFixedChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}> = ({ percentagemDiscount, coastFixed, disable, handlePercentagemDiscountChange, handleCoastFixedChange }) => {
  const { t } = useTranslation("promotion")

  const valuesToggle = ["Porcentagem", "Valor Fixado"]

  const { expansive, handleChangeState } = useExpansive(false)

  const { selectedType, handleToggleSelection } = useToggle([valuesToggle[0]])

  useEffect(() => {
    if (coastFixed === "") {
      return
    }

    const coastFixedNumber = Number(coastFixed)

    if (coastFixedNumber > 0) {
      handleToggleSelection(true, valuesToggle[1], () => {})
    } else {
      handleToggleSelection(true, valuesToggle[0], () => {})
    }
  }, [coastFixed])

  const handleChangeType = (e: string[]) => {
    if (e[0] === valuesToggle[0]) {
      handleCoastFixedChange({
        target: { value: "" },
      } as React.ChangeEvent<HTMLInputElement>)
    } else {
      handlePercentagemDiscountChange({
        target: { value: "" },
      } as React.ChangeEvent<HTMLInputElement>)
    }
  }

  return (
    <>
      <TitleChildrenPath
        icon={faMoneyBill}
        title={t("label_title_type_promotion")}
        boxShadow="none"
        iconColor={THEME.colors.iconBGSecondaryColor}
        subtitleChildren={
          <div className="flex items-center gap-2">
            <PopupHelpButton
              helpKey="pages.serivesdir.promotions.promotionspupop.2"
              title={t("helpPopup:sections.promotion.type.title")}
              description={t("helpPopup:sections.promotion.type.description")}
              bullets={t("helpPopup:sections.promotion.type.bullets", { returnObjects: true }) as string[]}
            />

            <ButtonIcon
              typeButton="button"
              sizeNumber={SIZE.buttonExpandIcon}
              bgColor={THEME.colors.buttonBGExpandColor}
              iconColor={THEME.colors.buttonFGExpandColor}
              icon={!expansive ? faAngleDown : faAngleUp}
              onClick={handleChangeState}
              size="small"
              descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.button.handleChangeState"
            />
          </div>
        }
        children={
          <>
            {expansive && (
              <>
                <GetSpace top={10} />

                <MessageList color={THEME.colors.cardMessageNoItem} icon={faWarning} message={t("label_only_type")} />

                <ToggleComponet
                  canSelectType={true}
                  selectedType={selectedType}
                  valuesToggle={valuesToggle}
                  handleChangeType={handleChangeType}
                  handleToggleSelection={handleToggleSelection}
                  disable={disable}
                  descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.toggle.toggle"
                />

                <GetSpace top={10} />

                {selectedType[0] === valuesToggle[1] ? (
                  <InputField
                    maxLength={INPUT_SIZE.PROMOTIONS.fixedValue}
                    type="number"
                    placeholder={t("label_person_fixed_place_holder")}
                    onInvalidMessage={t("label_person_fixed_onInvalidMessage")}
                    icon={faDollarSign}
                    value={coastFixed}
                    onChange={handleCoastFixedChange}
                    disable={disable}
                    descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.input.personFixedPlaceHolder"
                  />
                ) : (
                  <InputField
                    maxLength={INPUT_SIZE.PROMOTIONS.percentage}
                    type="number"
                    placeholder={t("label_person_percentage_place_holder")}
                    onInvalidMessage={t("label_person_percentage_onInvalidMessage")}
                    icon={faPercent}
                    value={percentagemDiscount}
                    onChange={handlePercentagemDiscountChange}
                    disable={disable}
                    descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.input.personPercentagePlaceHolder"
                  />
                )}
                <GetSpace top={5} />
              </>
            )}
          </>
        }
      />
    </>
  )
}

const DateData: React.FC<{
  dayWeek: EDayOfWeek[]
  timeStart: string
  timeFinish: string
  disable: boolean
  handleDayWeekChange: (e: EDayOfWeek[]) => void
  handleTimeFinishChange: (e: string) => void
  handleTimeStartChange: (e: string) => void
}> = ({
  dayWeek,
  timeStart,
  timeFinish,
  disable,
  handleDayWeekChange,
  handleTimeFinishChange,
  handleTimeStartChange,
}) => {
  const { t } = useTranslation("promotion")

  const { expansive, handleChangeState } = useExpansive()

  return (
    <>
      <TitleChildrenPath
        title={t("label_title_date_promotion")}
        iconColor={THEME.colors.iconBGSecondaryColor}
        icon={faCalendar}
        boxShadow="none"
        subtitleChildren={
          <div className="flex items-center gap-2">
            <PopupHelpButton
              helpKey="pages.serivesdir.promotions.promotionspupop.3"
              title={t("helpPopup:sections.promotion.date.title")}
              description={t("helpPopup:sections.promotion.date.description")}
              bullets={t("helpPopup:sections.promotion.date.bullets", { returnObjects: true }) as string[]}
            />

            <ButtonIcon
              typeButton="button"
              sizeNumber={SIZE.buttonExpandIcon}
              bgColor={THEME.colors.buttonBGExpandColor}
              iconColor={THEME.colors.buttonFGExpandColor}
              icon={!expansive ? faAngleDown : faAngleUp}
              onClick={handleChangeState}
              size="small"
              descriptionKey="interactionDescriptions:pages.serivesDir.promotions.promotionsPupop.button.handleChangeState2"
            />
          </div>
        }
        children={
          <>
            {expansive && (
              <>
                <div className={defaultStyle.divisionBar}></div>

                <DateTimeSelector
                  initialDayWeek={dayWeek}
                  initialTimeFinish={timeFinish}
                  initialTimeStart={timeStart}
                  onDaysChange={handleDayWeekChange}
                  onTimeStartChange={handleTimeStartChange}
                  onTimeEndChange={handleTimeFinishChange}
                  disable={disable}
                />
              </>
            )}
          </>
        }
      />
    </>
  )
}
